import { useCallback, useMemo } from "react";
import ModelCollection from "domains/collections/components/ModelCollection";
import { modelsFixedKeys } from "domains/models/api/endpoints";
import {
  getIsModelFluxDev,
  getIsModelFluxPro,
  getIsModelFluxPro1_1,
  getIsModelFluxPro1_1Ultra,
  getIsModelGptImage,
} from "domains/models/utils";
import { getApiCapabilities } from "domains/models/utils/capabilities";
import useRouter from "domains/navigation/hooks/useRouter";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import Alert from "domains/ui/components/Alert";
import Button from "domains/ui/components/Button";
import Divider from "domains/ui/components/Divider";
import Icon from "domains/ui/components/Icon";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { useHandleApiError } from "infra/api/error";
import {
  GetModelsByModelIdApiResponse,
  useDeleteModelsByModelIdMutation,
  usePostDownloadModelMutation,
  usePostModelsCopyByModelIdMutation,
  usePutModelsExamplesByModelIdMutation,
} from "infra/api/generated/api";

import { Box, Flex, Heading, Image, Text } from "@chakra-ui/react";
import { Menu, MenuButton, MenuList } from "@chakra-ui/react";

import ActionMenu from "./ActionMenu";
import EditableHeading from "./EditableHeading";

interface ModelShowHeaderProps {
  model: GetModelsByModelIdApiResponse["model"];
  onAddToComposition?: () => void;
  isOnLoraCatalog?: boolean;
}

export default function ModelShowHeader({
  model,
  onAddToComposition,
  isOnLoraCatalog = false,
}: ModelShowHeaderProps) {
  const router = useRouter();
  const { selectedProject } = useTeamContext();
  const { infoToast, successToast } = useScenarioToast();
  const handleApiError = useHandleApiError();
  const [deleteModelTrigger] = useDeleteModelsByModelIdMutation({
    fixedCacheKey: modelsFixedKeys.deleteModel,
  });
  const [copyModelMutation] = usePostModelsCopyByModelIdMutation();
  const [downloadModelMutation] = usePostDownloadModelMutation();
  const [putModelsExamplesByModelId] = usePutModelsExamplesByModelIdMutation();

  const isModelOwned = model?.ownerId === selectedProject.id;
  const isFromScenario = !isModelOwned && model.privacy === "public";
  const canRetrain =
    !model.type.includes("-composition") &&
    model.source === "scenario" &&
    isModelOwned;

  const isModelNew = model.status === "new";
  const isModelLora = model.type.includes("lora");
  const isModelTrained = model.status === "trained";
  const isModelOwnedAndPublic = isModelOwned && model.privacy === "public";
  const isOnLoraCatalogAndModelTrained =
    !isOnLoraCatalog && model.status === "trained";

  const isDividerRender =
    isModelTrained ||
    isModelNew ||
    isOnLoraCatalog ||
    isOnLoraCatalogAndModelTrained ||
    onAddToComposition;

  const isTextureModel = useMemo(() => {
    return model.tags?.includes("sc:texture");
  }, [model.tags]);
  const is3DModel = useMemo(() => {
    return model.capabilities.some((capability) =>
      getApiCapabilities("3d").includes(capability)
    );
  }, [model.capabilities]);
  const isVideoUpscaleModel = useMemo(() => {
    return model.tags?.includes("video-upscale");
  }, [model.tags]);
  const isVideoModel = useMemo(() => {
    return (
      !isVideoUpscaleModel &&
      model.capabilities.some((capability) =>
        getApiCapabilities("video").includes(capability)
      )
    );
  }, [model.capabilities, isVideoUpscaleModel]);

  // ----------------------------------

  const handleRemoveClick = useCallback(async () => {
    try {
      await deleteModelTrigger({
        modelId: model.id,
        projectId: selectedProject.id,
      }).unwrap();

      successToast({
        title: "Model deleted",
      });

      Track(AnalyticsEvents.Model.Deleted, {
        modelId: model.id,
      });

      void router.push({
        pathname: "/assets",
        query: { tab: "model" },
      });
    } catch (error) {
      handleApiError(error, "Error deleting model");
    }
  }, [
    deleteModelTrigger,
    model,
    selectedProject.id,
    successToast,
    router,
    handleApiError,
  ]);

  const handleCopyClick = useCallback(async () => {
    try {
      const newModel = await copyModelMutation({
        modelId: model.id,
        projectId: selectedProject.id,
        body: {
          copyAsTrained: false,
        },
      }).unwrap();
      Track(AnalyticsEvents.Model.Copied, {
        modelId: model.id,
        projectId: selectedProject.id,
      });
      successToast({
        title: "New model successfully created",
      });

      const newId = newModel?.model.id;
      if (newId) {
        void putModelsExamplesByModelId({
          modelId: newId,
          projectId: selectedProject.id,
          body: {
            assetIds: [],
          },
        });
        void router.push({
          pathname: "/models/new/[id]",
          query: { id: newId, retrain: "1" },
        });
      }
    } catch (error) {
      handleApiError(error, "Error creating new model");
    }
  }, [
    copyModelMutation,
    model,
    selectedProject.id,
    successToast,
    router,
    handleApiError,
    putModelsExamplesByModelId,
  ]);

  const handleCopyTrainedClick = useCallback(async () => {
    try {
      infoToast({
        title: "Model Duplication Ongoing",
        description: "Please wait a few seconds",
      });
      const newModel = await copyModelMutation({
        modelId: model.id,
        projectId: selectedProject.id,
        body: {
          copyAsTrained: true,
        },
      }).unwrap();
      Track(AnalyticsEvents.Model.CopiedTrained, {
        modelId: model.id,
        projectId: selectedProject.id,
      });
      successToast({
        title: "Model successfully duplicated",
      });

      const newModelId = newModel?.model.id;
      if (newModelId) {
        void router.push({
          pathname: "/models/[id]",
          query: { id: newModelId },
        });
      }
    } catch (error) {
      handleApiError(error, "Duplication failed");
    }
  }, [
    infoToast,
    copyModelMutation,
    model,
    selectedProject.id,
    successToast,
    router,
    handleApiError,
  ]);

  const handleExportClick = useCallback(async () => {
    try {
      await downloadModelMutation({
        modelId: model.id,
        projectId: selectedProject.id,
      }).unwrap();

      Track(AnalyticsEvents.Model.Exported, {
        modelId: model.id,
        projectId: selectedProject.id,
      });
      successToast({
        title: "Export Initiated",
        description:
          "A download link will be available via email and in the Notification tab.",
      });
    } catch (error) {
      handleApiError(error, "Export failed");
    }
  }, [
    model,
    selectedProject.id,
    successToast,
    handleApiError,
    downloadModelMutation,
  ]);

  const handleEditBlendClick = useCallback(() => {
    void router.push({
      pathname: "/compose/new",
      query: { modelId: model.id },
    });
  }, [model, router]);

  const canUseLiveMode = useMemo(
    () =>
      !getIsModelFluxDev(model) &&
      !getIsModelFluxPro(model) &&
      !getIsModelFluxPro1_1(model) &&
      !getIsModelFluxPro1_1Ultra(model) &&
      !getIsModelGptImage(model) &&
      !isTextureModel &&
      !is3DModel &&
      !isVideoModel &&
      !isVideoUpscaleModel,
    [model, isTextureModel, is3DModel, isVideoModel, isVideoUpscaleModel]
  );

  const generateLink = useMemo(() => {
    if (isTextureModel) {
      return "/textures/new";
    }
    if (is3DModel) {
      return "/3d/new";
    }
    if (isVideoUpscaleModel) {
      return "/videos/enhance";
    }
    if (isVideoModel) {
      return "/videos/new";
    }
    return "/images/new";
  }, [isTextureModel, is3DModel, isVideoModel, isVideoUpscaleModel]);

  // ----------------------------------

  return (
    <Flex direction="column" w="full">
      {isModelOwnedAndPublic && (
        <Alert
          mb={2}
          message="It should be edited with extreme caution."
          title="This is a public model"
          type="warning"
        />
      )}

      {isFromScenario && (
        <Flex align="center" mb={4} pt={4}>
          <Box
            mr={2}
            bg="backgroundSecondary.500"
            borderWidth={1}
            rounded="full"
          >
            <Image w="16px" m={2} alt="logo" src="/logos/scenario.svg" />
          </Box>
          <Text color="textSecondary" size="body.bolder.md">
            Scenario
          </Text>
        </Flex>
      )}

      <Flex align="center" justify="space-between" gap={2} w="full" pt={2}>
        {isModelOwned ? (
          <EditableHeading model={model} />
        ) : (
          <Heading size="lg">{model.name}</Heading>
        )}

        <Flex gap={2} textAlign="left">
          <ActionMenu
            isOnLoraCatalog={isOnLoraCatalog}
            canRetrain={canRetrain}
            model={model}
            onCopyClick={handleCopyClick}
            onCopyTrainedClick={handleCopyTrainedClick}
            onRemoveClick={handleRemoveClick}
            onEditBlendClick={handleEditBlendClick}
            onExportClick={handleExportClick}
          />
          {isModelOwned && (
            <>
              <ModelCollection models={[model]} menuPlacement="bottom-start" />
              {isDividerRender && (
                <Divider
                  mx={2}
                  dividerProps={{
                    width: "1px",
                    height: "100%",
                  }}
                />
              )}
            </>
          )}

          {isOnLoraCatalogAndModelTrained &&
            (canUseLiveMode || isModelLora ? (
              <Menu>
                <MenuButton
                  as={Button}
                  rightIcon={
                    <Icon id="Ui/ChevronDown" color="textPrimary" h="6px" />
                  }
                  size="sm"
                >
                  Use this model
                </MenuButton>
                <MenuList zIndex="popover" gap={2}>
                  <MenuItem
                    iconId="Ui/Image"
                    text="Generate"
                    internalLink={{
                      pathname: generateLink,
                      query: { modelId: model.id },
                    }}
                  />
                  {canUseLiveMode && (
                    <MenuItem
                      iconId="Domains/Canvas/Live"
                      text="Live"
                      internalLink={{
                        pathname: "/canvas/new",
                        query: { modelId: model.id, live: "true" },
                      }}
                    />
                  )}
                  {isModelLora && (
                    <MenuItem
                      iconId="Domains/Tools/Blend"
                      text="Compose"
                      internalLink={{
                        pathname: `/compose/new`,
                        query: { conceptToAdd: model.id },
                      }}
                    />
                  )}
                </MenuList>
              </Menu>
            ) : (
              <Button
                size="sm"
                internalLink={{
                  pathname: generateLink,
                  query: { modelId: model.id },
                }}
              >
                Use this model
              </Button>
            ))}

          {isModelNew && (
            <Button
              size="sm"
              internalLink={{
                pathname: `/models/new/[id]`,
                query: { id: model.id },
              }}
            >
              Complete setup
            </Button>
          )}

          {onAddToComposition && (
            <Button size="sm" onClick={onAddToComposition}>
              Add to composition
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
